import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { getCall } from '../hooks/Network';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { hasSubscriptionData } from '../hooks/Helper';
import { useDispatch, useSelector } from 'react-redux';
import { setBusinessHubId } from '../features/authentication/authSlice';
import { useTheme } from '@mui/material';
import configs from '../config/app';
import AssociateBackOffice from './customers/associateBackOffice';

// Corporate Customer Menu Component
const CorporateCustomerMenu = ({ subscriptions }) => {
  const { t } = useTranslation();
  const params = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const ApiKey = process.env.REACT_APP_API_KEY;
  const theme = useTheme();
  const clientId = Number(configs.clientId);
  // REDUX
  const sidebarExpanded = useSelector((state) => state.ui.sidebarExpanded);

  // Calculate BussinessHubAffiliateId in useEffect to avoid recalculation on every render
  const [businessHubAffiliateId, setBusinessHubAffiliateId] = useState(0);

  // Memoize the calculation to prevent unnecessary recalculations
  const calculatedBusinessHubId = useMemo(() => {
    if (!Array.isArray(subscriptions)) return 0;

    const allowedPlans =
      clientId === 3
        ? ['Affiliate Marketing Kit', 'Business Hub']
        : ['Affiliate Marketing Kit'];

    return hasSubscriptionData(subscriptions, allowedPlans)?.subscriptionId || 0;
  }, [subscriptions, clientId]);

  // Move all state updates to useEffect to avoid state updates during render
  // Use a debounced approach to prevent rapid Redux updates
  const updateBusinessHubId = useCallback(
    (newId) => {
      setBusinessHubAffiliateId(newId);
      dispatch(setBusinessHubId(newId));
      sessionStorage.setItem('BussinessHubId', newId);
    },
    [dispatch]
  );

  useEffect(() => {
    // Only update if the value has changed to prevent unnecessary re-renders
    if (calculatedBusinessHubId !== businessHubAffiliateId) {
      // Use setTimeout to defer the update and prevent render cycle conflicts
      const timeoutId = setTimeout(() => {
        updateBusinessHubId(calculatedBusinessHubId);
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [calculatedBusinessHubId, businessHubAffiliateId, updateBusinessHubId]);
  let customerId = params.customerId;

  const fnEcaSso = async (e) => {
    e.preventDefault();

    let sParams = {
      url: 'getSSOToken',
      urlParams: { AssociateId: params.customerId },
      headers: { 'API-key': ApiKey },
    };
    try {
      const resp = await getCall(sParams);
      if (resp.status === 200 && resp.data) {
        const envUrl = process.env.REACT_APP_FLAG_ACADEMY_URL;
        const clientId = process.env.REACT_APP_CLIENT_ID;
        // const ssoUrl = `${envUrl}?token=${resp.data}&clientId=${clientId}`;
        const ssoUrl = new URL(envUrl);
        ssoUrl.searchParams.set('token', resp.data);
        ssoUrl.searchParams.set('clientId', clientId);

        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

        // Try opening in new tab
        const newWindow = window.open(ssoUrl, '_blank', 'noopener,noreferrer');

        // If blocked
        if (
          isSafari &&
          (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined')
        ) {
          alert(t('popWasBlocked'));
        }
      } else {
        console.error('Error:', resp);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <>
      <ul className={`admin-navbar-nav navbar-nav mui-mode-${theme.palette.mode} mb-0`}>
        <li className={`sidebar-header ${!sidebarExpanded ? 'hide-text' : ''}`}>
          <div className='d-flex align-items-center '>
            <span className='me-2'>{t('user')}</span>
            <div
              className='flex-grow-1'
              style={{
                borderTop: '1px solid #80a0a9', // 👈 your custom color
                opacity: 0.6,
              }}
            ></div>
          </div>
        </li>
        <AssociateBackOffice subscriptions={subscriptions} />
        <li className={`sidebar-header ${!sidebarExpanded ? 'hide-text' : ''}`}>
          <div className='d-flex align-items-center '>
            <span className='me-2'>{t('admin')}</span>
            <div
              className='flex-grow-1'
              style={{
                borderTop: '1px solid #80a0a9', // 👈 your custom color
                opacity: 0.6,
              }}
            ></div>
          </div>
          
        </li>
        <li className={`nav-item ${location.pathname == `/customers` ? 'active' : ''}`}>
          <a href={`/customers`} className='nav-link'>
            <span className='nav-link-icon d-md-none d-lg-inline-block'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='icon icon-thick icon-tabler icon-tabler-layout-dashboard'
                width='40'
                height='40'
                viewBox='0 0 24 24'
                strokeWidth='1'
                stroke='currentColor'
                fill='none'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none'></path>
                <path d='M4 4h6v8h-6z'></path>
                <path d='M4 16h6v4h-6z'></path>
                <path d='M14 12h6v8h-6z'></path>
                <path d='M14 4h6v4h-6z'></path>
              </svg>
            </span>
            <span className={`nav-link-title ${!sidebarExpanded ? 'hide-text' : ''}`}>
              {t('mainMenu')}
            </span>
          </a>
        </li>
        <li
          className={`nav-item ${location.pathname == `/customers/${customerId}/summary` ? 'active' : ''}`}
        >
          <Link className='nav-link' to={`/customers/${customerId}/summary`}>
            <span className='nav-link-icon d-md-none d-lg-inline-block'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='icon icon-thick icon-tabler icon-tabler-layout-dashboard'
                width='40'
                height='40'
                viewBox='0 0 24 24'
                strokeWidth='1'
                stroke='currentColor'
                fill='none'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none'></path>
                <path d='M4 4h6v8h-6z'></path>
                <path d='M4 16h6v4h-6z'></path>
                <path d='M14 12h6v8h-6z'></path>
                <path d='M14 4h6v4h-6z'></path>
              </svg>
            </span>
            <span className={`nav-link-title ${!sidebarExpanded ? 'hide-text' : ''}`}>
              {t('summary')}
            </span>
          </Link>
        </li>
      </ul>
    </>
  );
};

CorporateCustomerMenu.propTypes = {
  subscriptions: PropTypes.any.isRequired,
};

export default CorporateCustomerMenu;
